/**
 * DuelSceneFilter — a scale-aware pixel-art upscaling PostFX pipeline for
 * the in-world duel (owner ask 2026-08-03: filter the WHOLE 5x scene, and
 * let me cycle smoothing styles live to find one I like — xBRZ was too
 * blobby). TESTING-PHASE surface behind DUEL_SCENE_FILTER_ENABLED.
 *
 * The trick (research synthesis 2026-08-03): a Phaser camera PostFX sees the
 * ALREADY-ZOOMED framebuffer. But the duel zoom is an exact INTEGER with
 * NEAREST filtering (the crispness invariant), so each source texel is a
 * solid Z×Z block — a lossless copy of the native pixel. The shader recovers
 * the native neighborhood by sampling the framebuffer at ±Z-px offsets
 * (moving exactly one block preserves the in-block position, so it lands in
 * the neighbor texel regardless of sub-pixel phase), runs a pixel-art scaler
 * on that native neighborhood, and emits the sub-texel the current fragment
 * falls in. cam.zoom stays untouched, so every DOM overlay keeps working.
 *
 * Algorithms (uAlgo): 0 = off (passthrough), 1 = MMPX (McGuire & Gagiu, MIT),
 * 2 = Scale2x/AdvMAME, 3 = xBR-lv2 (Hyllian, MIT — edge-directed smooth
 * curves), 4 = Scale3x/AdvMAME3x (Mazzoleni).
 *
 * WHY SCALE3X IS THE ANSWER TO "less blocky" (owner ask 2026-08-04: "Scale2x
 * has the best looking combat filter... I wish there was a way to get it less
 * blocky"). Two measured facts settle it. (a) The framebuffer is CSS px —
 * Scale.RESIZE with no `zoom`/`autoRound` and no devicePixelRatio anywhere in
 * Phaser 3, so a portrait iPhone is ~393×852 and the duel zoom is Z = 6, i.e.
 * a Scale2x sub-texel is Z/2 = 3.0 framebuffer px. (b) `pixelArt: true` makes
 * Phaser stamp `image-rendering: pixelated` INLINE on the canvas element
 * (CreateRenderer.js:86 → CanvasInterpolation.setCrisp), so on a DPR-3 phone
 * each of those 3 px is a HARD 9-device-px block and nothing in the frame has
 * ever been antialiased. What reads as blocky is therefore the TREAD LENGTH,
 * not the riser hardness — and only a denser rule grid shortens the tread.
 * Scale3x costs the same nine taps this shader already fetches, emits
 * E4 = E UNCONDITIONALLY (so it provably cannot destroy a source pixel — the
 * anti-blob guarantee xBRZ could not give), and drops the sub-texel to
 * Z/3 = 2.0 px. N = 3 also divides Z = 6 and Z = 9 exactly, which retires the
 * uneven-staircase artifact at odd zooms for free, without touching duelZoom.
 *
 * REJECTED, with reasons, so nobody re-derives them: a second AA render pass
 * (Phaser genuinely supports it — two shaders + a custom onDraw ping-ponging
 * through fullFrame1, the BlurFXPipeline idiom — but at Z = 6 there are only
 * three framebuffer samples per cell, so the dial has three usable positions
 * and every ramp pixel is still delivered as a hard 3-device-px band: it buys
 * a tone level, not a shape, and the compositor route below buys the same
 * thing for free); Scale4x (its Z/4 = 1.5 px cell is below what the
 * framebuffer can hold evenly at Z = 6 — point sampling renders alternating
 * 1px/2px cells, reproducing at 4x density the exact unevenness we are
 * removing; it only pays off at Z >= 8, i.e. desktop); hq4x / Cleanedge /
 * super-xBR / Omniscale / ScaleFX (all continuous-blend edge-directed, the
 * family the owner has now voted against twice — verbally on xBRZ and by
 * preferring Scale2x to the xBR-lv2 already on his pill). The riser softening
 * the ask ALSO wants is delivered by duelFilterCycler's `smooth` pill, which
 * hands the canvas→device upscale back to the compositor — see that file.
 */
import Phaser from 'phaser';

export const DUEL_FILTER_ALGOS = [
  'off',
  'MMPX',
  'Scale2x',
  'xBR',
  // APPEND ONLY. duelFilterCycler persists the ARRAY INDEX in localStorage
  // and the index is passed straight through as uAlgo, so inserting an entry
  // silently re-points every stored pref and every hard-coded `uAlgo ==` test.
  'Scale3x',
] as const;
export type DuelFilterAlgo = (typeof DUEL_FILTER_ALGOS)[number];
export const DUEL_FILTER_PIPELINE_KEY = 'DuelSceneFilter';

/** Sub-texels per source texel each algorithm emits — the grid density that
 *  sets TREAD LENGTH. 0 = continuous (no sub-grid: `off` passes through,
 *  xBR-lv2 blends at full framebuffer resolution), so cell size is undefined
 *  for those.
 *
 *  Parallel to DUEL_FILTER_ALGOS by index; APPEND ONLY for the same reason.
 *
 *  Load-bearing once the cycler runs OUTSIDE a duel (2026-08-04): a rule
 *  scaler can only resolve its sub-grid if a cell is at least one framebuffer
 *  pixel, i.e. zoom Z >= N. The duel punches to Z = 6 so everything fits, but
 *  the OVERWORLD sits at Z = 2 (portrait baseline, `computeZoom`), where
 *  Scale3x's cell is 0.67 px — point sampling then renders an uneven 1/1/0
 *  pattern instead of thirds, which reads as a broken filter rather than a
 *  subtle one. The cycler pill surfaces Z/N so that is visible instead of
 *  mysterious; it deliberately does NOT hide under-resolved algorithms, since
 *  seeing where each one falls apart is the point of a taste-gate surface. */
export const DUEL_FILTER_GRID: readonly number[] = [0, 2, 2, 0, 3];

const FRAG = /* glsl */ `
precision highp float;
uniform sampler2D uMainSampler;
uniform vec2 uResolution;   // framebuffer px
uniform float uZoom;        // integer duel zoom Z (screen px per source texel)
uniform vec2 uPhase;        // sub-texel origin of the native grid, SCREEN-y-DOWN px
uniform int uAlgo;          // 0 off, 1 MMPX, 2 Scale2x, 3 xBR-lv2, 4 Scale3x
varying vec2 outTexCoord;

vec2 gBase;  // block center of the current fragment's source texel (texcoord)
vec2 gUnit;  // texcoord delta per one source texel (= Z / resolution)

// Sample the source texel dx,dy away, from BLOCK CENTERS.
//
// The original rationale for block-center snapping — "the PostFX render
// target is LINEAR-filtered" — is WRONG, verified in Phaser 3.90 source
// 2026-08-04: RenderTarget requests minFilter 0 (= LINEAR), but
// WebGLRenderer.createTextureFromSource:2177 only honours that request inside
// \`if (scaleMode === LINEAR && this.config.antialias)\`, and main.ts's
// \`pixelArt: true\` forces antialias false (Config.js:403-407). Every render
// target in this game is NEAREST/NEAREST; hardware bilinear does not exist on
// this chain, which is also why no smoothing scheme here may lean on it.
//
// The snapping stays, because the real reason is stronger and
// filter-INDEPENDENT: centerPx is what defines \`f\`, the sub-texel coordinate
// that the 2x quadrant pick, scale3x() and xbrLv2() all consume. A
// fragment-relative origin is what produced the unsmoothed "islands" in the
// first phone dogfood (commit 2a7d090) — do not "simplify" it away.
vec4 S(float dx, float dy) {
  return texture2D(uMainSampler, gBase + vec2(dx * gUnit.x, dy * gUnit.y));
}

bool eqc(vec4 a, vec4 b) { return all(lessThanEqual(abs(a - b), vec4(0.004))); }
bool nec(vec4 a, vec4 b) { return !eqc(a, b); }
bool aeq2(vec4 b, vec4 a0, vec4 a1) { return eqc(b, a0) && eqc(b, a1); }
bool aeq3(vec4 b, vec4 a0, vec4 a1, vec4 a2) { return eqc(b, a0) && eqc(b, a1) && eqc(b, a2); }
bool aeq4(vec4 b, vec4 a0, vec4 a1, vec4 a2, vec4 a3) { return eqc(b, a0) && eqc(b, a1) && eqc(b, a2) && eqc(b, a3); }
bool yeq3(vec4 b, vec4 a0, vec4 a1, vec4 a2) { return eqc(b, a0) || eqc(b, a1) || eqc(b, a2); }
bool neq2(vec4 b, vec4 a0, vec4 a1) { return nec(b, a0) && nec(b, a1); }
bool neq4(vec4 b, vec4 a0, vec4 a1, vec4 a2, vec4 a3) { return nec(b, a0) && nec(b, a1) && nec(b, a2) && nec(b, a3); }

// Matches the C reference luma(): (R+G+B+1) * (256 - alpha), channels 0..255.
float luma(vec4 c) {
  float af = 256.0 - c.a * 255.0;
  return ((c.r + c.g + c.b) * 255.0 + 1.0) * af;
}

// ── Scale3x / AdvMAME3x (Mazzoleni, scale2x.sourceforge.net/algorithm.html) ──
// Nine taps — exactly the A..I the MMPX path already fetches — plus ten colour
// comparisons. The four corner predicates are Scale2x's guarded form
// re-expressed (under D == B, B != H <=> D != H and D != F <=> B != F), so this
// fires on byte-identically the diagonals Scale2x fires on; it just resolves
// them onto a 3x3 sub-grid instead of 2x2, halving the tread. E4 = E has NO
// exception — the source texel survives untouched at the centre of its own
// block, which is what makes Scale3x provably non-destructive.
//
// Edge-midpoint guard law, for anyone checking the transcription: for the
// midpoint of edge e fired by adjacent corner c, the extra guard is
// "E != the diagonal at the FAR end of e". E1 (top) from cTL guards E != C,
// from cTR guards E != A; E7 (bottom) from cBL guards E != I, from cBR guards
// E != G. The rule set is closed under the full dihedral group, which is what
// makes it safe to run in this shader's vertically-mirrored frame (see main).
vec4 scale3x(vec2 f, vec4 A, vec4 B, vec4 C,
                     vec4 D, vec4 E, vec4 F,
                     vec4 G, vec4 H, vec4 I) {
  bool cTL = eqc(D, B) && nec(B, F) && nec(D, H);
  bool cTR = eqc(B, F) && nec(B, D) && nec(F, H);
  bool cBL = eqc(H, D) && nec(D, B) && nec(H, F);
  bool cBR = eqc(F, H) && nec(D, F) && nec(B, H);

  // clamp guards f == 1.0, which (fpx - blockLeft)/uZoom can reach exactly at
  // a block's far edge under float rounding — floor(3.0) would index a
  // nonexistent fourth column. The 2x quadrant pick is immune to this because
  // it only ever compares against 0.5, so it is a genuinely new hazard here.
  vec2 g = clamp(floor(f * 3.0), 0.0, 2.0);

  if (g.y < 0.5) {                                                          // top row
    if (g.x < 0.5) return cTL ? D : E;                                      // E0
    if (g.x < 1.5) return ((cTL && nec(E, C)) || (cTR && nec(E, A))) ? B : E; // E1
    return cTR ? F : E;                                                     // E2
  }
  if (g.y < 1.5) {                                                          // middle row
    if (g.x < 0.5) return ((cTL && nec(E, G)) || (cBL && nec(E, A))) ? D : E; // E3
    if (g.x < 1.5) return E;                                                // E4 — always
    return ((cTR && nec(E, I)) || (cBR && nec(E, C))) ? F : E;              // E5
  }
  if (g.x < 0.5) return cBL ? D : E;                                        // E6
  if (g.x < 1.5) return ((cBR && nec(E, G)) || (cBL && nec(E, I))) ? H : E; // E7
  return cBR ? F : E;                                                       // E8
}

// ── xBR-lv2 (Hyllian, MIT; CORNER_C, small-details path so f4 = 0) ──
// Ported from libretro's xbr-lv2.glsl and verified offline against a Route1
// still before shipping. Edge-directed continuous blend — the smooth-curves
// look, milder than the xBRZ bake the owner disliked. Uses the block-center
// S() like the rest of the filter; fp is the shared sub-texel coord.
float xdf3(vec3 a, vec3 b) { return abs(a.r - b.r) + abs(a.g - b.g) + abs(a.b - b.b); }
vec4 xdf(vec4 a, vec4 b) { return abs(a - b); }
vec4 xdiff(vec4 a, vec4 b) { return vec4(notEqual(a, b)); }
vec4 xeq(vec4 a, vec4 b) { return step(xdf(a, b), vec4(15.0)); }
vec4 xneq(vec4 a, vec4 b) { return vec4(1.0) - xeq(a, b); }
vec4 xwd(vec4 a, vec4 b, vec4 c, vec4 d, vec4 e, vec4 f, vec4 g, vec4 h) {
  return xdf(a, b) + xdf(a, c) + xdf(d, e) + xdf(d, f) + 4.0 * xdf(g, h);
}

vec3 xbrLv2(vec2 fp) {
  const vec3 rgbw = vec3(14.352, 28.176, 5.472);
  const float XBR_SCALE = 3.0, lv2cf = 2.0;
  vec3 A1 = S(-1.0, -2.0).rgb, B1 = S(0.0, -2.0).rgb, C1 = S(1.0, -2.0).rgb;
  vec3 A = S(-1.0, -1.0).rgb, B = S(0.0, -1.0).rgb, C = S(1.0, -1.0).rgb;
  vec3 Dc = S(-1.0, 0.0).rgb, E = S(0.0, 0.0).rgb, F = S(1.0, 0.0).rgb;
  vec3 G = S(-1.0, 1.0).rgb, Hc = S(0.0, 1.0).rgb, I = S(1.0, 1.0).rgb;
  vec3 G5 = S(-1.0, 2.0).rgb, H5 = S(0.0, 2.0).rgb, I5 = S(1.0, 2.0).rgb;
  vec3 A0 = S(-2.0, -1.0).rgb, D0 = S(-2.0, 0.0).rgb, G0 = S(-2.0, 1.0).rgb;
  vec3 C4 = S(2.0, -1.0).rgb, F4 = S(2.0, 0.0).rgb, I4 = S(2.0, 1.0).rgb;
  vec4 Ao = vec4(1.0, -1.0, -1.0, 1.0), Bo = vec4(1.0, 1.0, -1.0, -1.0), Co = vec4(1.5, 0.5, -0.5, 0.5);
  vec4 Ax = vec4(1.0, -1.0, -1.0, 1.0), Bx = vec4(0.5, 2.0, -0.5, -2.0), Cx = vec4(1.0, 1.0, -0.5, 0.0);
  vec4 Ay = vec4(1.0, -1.0, -1.0, 1.0), By = vec4(2.0, 0.5, -2.0, -0.5), Cy = vec4(2.0, 0.0, -1.0, 0.5);
  vec4 Ci = vec4(0.25);
  vec4 delta = vec4(1.0 / XBR_SCALE);
  vec4 delta_l = vec4(0.5 / XBR_SCALE, 1.0 / XBR_SCALE, 0.5 / XBR_SCALE, 1.0 / XBR_SCALE);
  vec4 delta_u = delta_l.yxwz;
  vec4 b = vec4(dot(B, rgbw), dot(Dc, rgbw), dot(Hc, rgbw), dot(F, rgbw));
  vec4 c = vec4(dot(C, rgbw), dot(A, rgbw), dot(G, rgbw), dot(I, rgbw));
  vec4 d = b.yzwx, e = vec4(dot(E, rgbw)), f = b.wxyz, g = c.zwxy, h = b.zwxy, i = c.wxyz;
  vec4 i4 = vec4(dot(I4, rgbw), dot(C1, rgbw), dot(A0, rgbw), dot(G5, rgbw));
  vec4 i5 = vec4(dot(I5, rgbw), dot(C4, rgbw), dot(A1, rgbw), dot(G0, rgbw));
  vec4 h5 = vec4(dot(H5, rgbw), dot(F4, rgbw), dot(B1, rgbw), dot(D0, rgbw));
  vec4 f4 = vec4(0.0);
  vec4 fx = Ao * fp.y + Bo * fp.x, fx_l = Ax * fp.y + Bx * fp.x, fx_u = Ay * fp.y + By * fp.x;
  vec4 irlv0 = xdiff(e, f) * xdiff(e, h);
  vec4 irlv1 = irlv0 * (xneq(f, b) * xneq(f, c) + xneq(h, d) * xneq(h, g)
    + xeq(e, i) * (xneq(f, f4) * xneq(f, i4) + xneq(h, h5) * xneq(h, i5)) + xeq(e, g) + xeq(e, c));
  vec4 irlv2l = xdiff(e, g) * xdiff(d, g);
  vec4 irlv2u = xdiff(e, c) * xdiff(b, c);
  vec4 fx45i = clamp((fx + delta - Co - Ci) / (2.0 * delta), 0.0, 1.0);
  vec4 fx45 = clamp((fx + delta - Co) / (2.0 * delta), 0.0, 1.0);
  vec4 fx30 = clamp((fx_l + delta_l - Cx) / (2.0 * delta_l), 0.0, 1.0);
  vec4 fx60 = clamp((fx_u + delta_u - Cy) / (2.0 * delta_u), 0.0, 1.0);
  vec4 wd1 = xwd(e, c, g, i, h5, f4, h, f);
  vec4 wd2 = xwd(h, d, i5, f, i4, b, e, i);
  vec4 edri = step(wd1, wd2) * irlv0;
  vec4 edr = step(wd1 + vec4(0.1), wd2) * step(vec4(0.5), irlv1);
  vec4 edr_l = step(lv2cf * xdf(f, g), xdf(h, c)) * irlv2l * edr;
  vec4 edr_u = step(lv2cf * xdf(h, c), xdf(f, g)) * irlv2u * edr;
  fx45 = edr * fx45; fx30 = edr_l * fx30; fx60 = edr_u * fx60; fx45i = edri * fx45i;
  vec4 px = step(xdf(e, f), xdf(e, h));
  vec4 maximos = max(max(fx30, fx60), max(fx45, fx45i));
  vec3 res1 = E;
  res1 = mix(res1, mix(Hc, F, px.x), maximos.x);
  res1 = mix(res1, mix(B, Dc, px.z), maximos.z);
  vec3 res2 = E;
  res2 = mix(res2, mix(F, B, px.y), maximos.y);
  res2 = mix(res2, mix(Dc, Hc, px.w), maximos.w);
  return mix(res1, res2, step(xdf3(E, res1), xdf3(E, res2)));
}

void main() {
  if (uAlgo == 0) { gl_FragColor = texture2D(uMainSampler, outTexCoord); return; }

  vec2 res = uResolution;
  vec2 fpx = outTexCoord * res;

  // VERTICAL PHASE CORRECTION (bug found 2026-08-04; pre-existing since the
  // filter landed). WorldScene computes uPhase in SCREEN-y-DOWN coords
  // (mod(-cam.worldView.y * z, z)), but outTexCoord.y runs BOTTOM-UP: the
  // scene renders into the PostFX target under the UNFLIPPED projection
  // ortho(0, w, h, 0) (WebGLPipeline.js:1038 — flipProjectionMatrix is called
  // ONLY by PreFXPipeline, never on this path), so screen-top lands in the
  // target's LAST row; PostFX's quad then maps uv(0,0) -> clip(-1,-1) and it
  // comes back upright. With sy = res.y - fy, native grid lines sitting at
  // sy = uPhase.y (mod Z) land at fy = res.y - uPhase.y (mod Z).
  //
  // Without this the recovered neighbourhood is still internally VALID (the
  // samples are exactly Z apart, so the rules fire on real texel colours) —
  // but every rule output is PAINTED up to Z-1 px away from the art it was
  // derived from. That is a vertical misregistration between the filtered
  // frame, the unfiltered frame, and every worldToScreen-positioned Zone-2
  // overlay, and it varies with viewport height, so it differs per device and
  // per orientation. Exactly the shape of an intermittent "looks off on the
  // phone" report; fix it BEFORE any smoothing A/B, or the A/B is run on a
  // grid that is already off register.
  //
  // The vertical MIRROR the same flip implies is deliberately left alone.
  // Scale2x / Scale3x / MMPX / xBR-lv2 are all closed under reflection, and
  // f.y flips consistently with B/H, so every rule fires identically in the
  // mirrored frame (hand-checked for Scale2x's four corners and MMPX's 1:1
  // slope group). Only the phase was ever wrong. Debug probe if this is ever
  // in doubt: emit vec4(vec3(step(0.5, f.y)), 1.0) and confirm the bands land
  // on the art's texel rows — the x axis has no flip and is the control.
  vec2 phase = vec2(uPhase.x, mod(res.y - uPhase.y, uZoom));

  // Snap to the current source texel's block center (see S()).
  vec2 cell = floor((fpx - phase) / uZoom);
  vec2 centerPx = phase + (cell + 0.5) * uZoom;
  gBase = centerPx / res;
  gUnit = vec2(uZoom) / res;

  // Sub-texel position of this fragment within its block, [0,1).
  vec2 f = (fpx - (centerPx - 0.5 * uZoom)) / uZoom;

  // xBR is a continuous-scale blend — it returns the final color directly
  // from fp (no 2x quadrant pick). Alpha is clamped to the blended RGB:
  // neighbour-blended RGB carrying the CENTER texel's alpha can emit rgb > a,
  // an invalid premultiplied colour (Phaser's context is
  // premultipliedAlpha:true). Unreachable while the tilemap fills the duel
  // frame; free insurance if anything transparent ever reaches this camera.
  if (uAlgo == 3) {
    vec3 xc = xbrLv2(f);
    float xa = S(0.0, 0.0).a;
    gl_FragColor = vec4(min(xc, vec3(xa)), xa);
    return;
  }

  // PER-ALGORITHM TAP OWNERSHIP (2026-08-04). uAlgo is a uniform, so these
  // branches are wavefront-coherent and only the taken path's texture fetches
  // actually execute — but anything hoisted ABOVE the dispatch is paid by
  // everyone. Scale2x's rule reads only the PLUS (B/D/E/F/H); the shared 3x3
  // that used to sit here made the owner's preferred algorithm pay four dead
  // dependent taps per fragment, 44% of its tap traffic. Keep every branch
  // exhaustive and early-returning so a new algorithm can never fall through
  // into MMPX's body and get billed for it.
  if (uAlgo == 2) {
    // Scale2x / AdvMAME2x — 5 taps.
    vec4 B = S(0.0, -1.0);
    vec4 D = S(-1.0, 0.0), E = S(0.0, 0.0), F = S(1.0, 0.0);
    vec4 H = S(0.0, 1.0);
    vec4 J = E, K = E, L = E, M = E; // top-left, top-right, bottom-left, bottom-right
    if (nec(B, H) && nec(D, F)) {
      J = eqc(D, B) ? D : E;
      K = eqc(B, F) ? F : E;
      L = eqc(D, H) ? D : E;
      M = eqc(H, F) ? F : E;
    }
    gl_FragColor = (f.x < 0.5) ? (f.y < 0.5 ? J : L) : (f.y < 0.5 ? K : M);
    return;
  }

  vec4 A = S(-1.0, -1.0), B = S(0.0, -1.0), C = S(1.0, -1.0);
  vec4 D = S(-1.0, 0.0),  E = S(0.0, 0.0),  F = S(1.0, 0.0);
  vec4 G = S(-1.0, 1.0),  H = S(0.0, 1.0),  I = S(1.0, 1.0);

  // Scale3x — same nine taps, 3x3 sub-grid, no quadrant pick.
  if (uAlgo == 4) { gl_FragColor = scale3x(f, A, B, C, D, E, F, G, H, I); return; }

  // MMPX (uAlgo == 1) — the nine above plus up to 12 more inside the rules.
  vec4 J = E, K = E, L = E, M = E; // top-left, top-right, bottom-left, bottom-right
  if (nec(A, E) || nec(B, E) || nec(C, E) || nec(D, E) || nec(F, E) || nec(G, E) || nec(H, E) || nec(I, E)) {
    vec4 P = S(0.0, -2.0), Sd = S(0.0, 2.0), Q = S(-2.0, 0.0), R = S(2.0, 0.0);
    float Bl = luma(B), Dl = luma(D), El = luma(E), Fl = luma(F), Hl = luma(H);

    // 1:1 slope
    if ((eqc(D, B) && nec(D, H) && nec(D, F)) && (El >= Dl || eqc(E, A)) && yeq3(E, A, C, G) && ((El < Dl) || nec(A, D) || nec(E, P) || nec(E, Q))) J = D;
    if ((eqc(B, F) && nec(B, D) && nec(B, H)) && (El >= Bl || eqc(E, C)) && yeq3(E, A, C, I) && ((El < Bl) || nec(C, B) || nec(E, P) || nec(E, R))) K = B;
    if ((eqc(H, D) && nec(H, F) && nec(H, B)) && (El >= Hl || eqc(E, G)) && yeq3(E, A, G, I) && ((El < Hl) || nec(G, H) || nec(E, Sd) || nec(E, Q))) L = H;
    if ((eqc(F, H) && nec(F, B) && nec(F, D)) && (El >= Fl || eqc(E, I)) && yeq3(E, C, G, I) && ((El < Fl) || nec(I, H) || nec(E, R) || nec(E, Sd))) M = F;

    // Intersection
    if (nec(E, F) && aeq4(E, C, I, D, Q) && aeq2(F, B, H) && nec(F, S(3.0, 0.0))) { K = F; M = F; }
    if (nec(E, D) && aeq4(E, A, G, F, R) && aeq2(D, B, H) && nec(D, S(-3.0, 0.0))) { J = D; L = D; }
    if (nec(E, H) && aeq4(E, G, I, B, P) && aeq2(H, D, F) && nec(H, S(0.0, 3.0))) { L = H; M = H; }
    if (nec(E, B) && aeq4(E, A, C, H, Sd) && aeq2(B, D, F) && nec(B, S(0.0, -3.0))) { J = B; K = B; }
    if (Bl < El && aeq4(E, G, H, I, Sd) && neq4(E, A, D, C, F)) { J = B; K = B; }
    if (Hl < El && aeq4(E, A, B, C, P) && neq4(E, D, G, I, F)) { L = H; M = H; }
    if (Fl < El && aeq4(E, A, D, G, Q) && neq4(E, B, C, I, H)) { K = F; M = F; }
    if (Dl < El && aeq4(E, C, F, I, R) && neq4(E, B, A, G, H)) { J = D; L = D; }

    // 2:1 slope
    if (nec(H, B)) {
      if (nec(H, A) && nec(H, E) && nec(H, C)) {
        if (aeq3(H, G, F, R) && neq2(H, D, S(2.0, -1.0))) L = M;
        if (aeq3(H, I, D, Q) && neq2(H, F, S(-2.0, -1.0))) M = L;
      }
      if (nec(B, I) && nec(B, G) && nec(B, E)) {
        if (aeq3(B, A, F, R) && neq2(B, D, S(2.0, 1.0))) J = K;
        if (aeq3(B, C, D, Q) && neq2(B, F, S(-2.0, 1.0))) K = J;
      }
    }
    if (nec(F, D)) {
      if (nec(D, I) && nec(D, E) && nec(D, C)) {
        if (aeq3(D, A, H, Sd) && neq2(D, B, S(1.0, 2.0))) J = L;
        if (aeq3(D, G, B, P) && neq2(D, H, S(1.0, -2.0))) L = J;
      }
      if (nec(F, E) && nec(F, A) && nec(F, G)) {
        if (aeq3(F, C, H, Sd) && neq2(F, B, S(-1.0, 2.0))) K = M;
        if (aeq3(F, I, B, P) && neq2(F, H, S(-1.0, -2.0))) M = K;
      }
    }
  }

  // MMPX is a 2x rule scaler — pick the sub-texel by quadrant.
  gl_FragColor = (f.x < 0.5) ? (f.y < 0.5 ? J : L) : (f.y < 0.5 ? K : M);
}
`;

export class DuelSceneFilterPipeline extends Phaser.Renderer.WebGL.Pipelines
  .PostFXPipeline {
  /** Index into DUEL_FILTER_ALGOS, passed straight through as uAlgo:
   *  0 off, 1 MMPX, 2 Scale2x, 3 xBR-lv2, 4 Scale3x. Updated live by the
   *  cycler. */
  algo = 0;
  /** Integer duel zoom (screen px per source texel), pushed each frame. */
  zoom = 2;
  /** Native-grid sub-texel origin in SCREEN-y-DOWN framebuffer px, pushed
   *  each frame. The shader flips phaseY into the bottom-up texcoord frame
   *  itself — see the phase-correction note in main(). Kept screen-down here
   *  because that is the frame WorldScene's worldView math naturally lives
   *  in, and it is the same frame worldToScreen positions Zone-2 DOM in. */
  phaseX = 0;
  phaseY = 0;

  constructor(game: Phaser.Game) {
    // SINGLE SHADER, SINGLE PASS — deliberate. A two-shader pipeline is
    // supported (config.shaders + a custom onDraw), but it makes every
    // no-arg set*() in onPreRender ambiguous: WebGLShader.setUniform1
    // reassigns pipeline.currentShader as a side effect, so from frame 2
    // uniforms would land in whichever shader was bound last. Staying
    // single-shader keeps onPreRender's no-arg form correct by construction.
    super({ game, name: DUEL_FILTER_PIPELINE_KEY, fragShader: FRAG });
  }

  override onPreRender(): void {
    this.set1i('uAlgo', this.algo);
    this.set1f('uZoom', this.zoom);
    this.set2f('uPhase', this.phaseX, this.phaseY);
    this.set2f('uResolution', this.renderer.width, this.renderer.height);
  }
}
